/* Motion primitives.
 *
 * Everything gesture-driven on this site runs through here, because CSS
 * transitions and @keyframes cannot do the two things that make an interface
 * feel physical: start from the value that is currently ON SCREEN, and carry
 * the velocity the finger had when it let go.
 *
 * The spring is integrated per frame rather than solved in closed form, which
 * is what makes it interruptible for free — velocity is a live state variable,
 * so re-targeting mid-flight continues from the current position AND the
 * current speed instead of cutting to a new curve.
 *
 * Parameters are Apple's designer-facing pair, not mass/stiffness/damping:
 *   damping  1.0 = critically damped, no overshoot. < 1.0 overshoots.
 *   response how quickly it reaches the target, in seconds. NOT a duration —
 *            a spring has no fixed duration; settle time emerges.
 */

export const SPRING = {
  /* House default: graceful, never bouncy. */
  default: { damping: 1.0, response: 0.35 },
  /* Reposition — the value Apple ships for moving Picture-in-Picture. */
  move: { damping: 1.0, response: 0.4 },
  /* Only when the gesture itself carried momentum (a flick, a throw). */
  momentum: { damping: 0.82, response: 0.4 },
  /* Drawers and sheets. */
  sheet: { damping: 0.85, response: 0.3 },
};

const MAX_DT = 1 / 30; // never integrate a whole dropped-frame gap at once

/* Animate `from` -> `to`, continuing at `velocity` (units/second).
 * Returns a handle: .stop() halts and reports the live value,
 *                   .retarget(to, addVelocity) redirects WITHOUT a velocity
 *                   discontinuity — the "brick wall" you get from replacing
 *                   one animation with another at a reversal. */
export function spring({
  from,
  to,
  velocity = 0,
  damping = SPRING.default.damping,
  response = SPRING.default.response,
  onUpdate,
  onComplete,
  precision = 0.05,
}) {
  let x = from;
  let v = velocity;
  let target = to;
  let raf = null;
  let last = null;
  let done = false;

  const w0 = (2 * Math.PI) / response;
  const k = w0 * w0;
  const c = 2 * damping * w0;

  const step = (now) => {
    if (done) return;
    if (last == null) last = now;
    let dt = (now - last) / 1000;
    last = now;
    if (dt <= 0) {
      raf = requestAnimationFrame(step);
      return;
    }
    // Sub-step so a long frame stays stable instead of exploding.
    let remaining = dt;
    while (remaining > 0) {
      const h = Math.min(remaining, MAX_DT);
      const a = -k * (x - target) - c * v;
      v += a * h;
      x += v * h;
      remaining -= h;
    }

    if (Math.abs(x - target) < precision && Math.abs(v) < precision * 10) {
      x = target;
      v = 0;
      done = true;
      if (onUpdate) onUpdate(x, v);
      if (onComplete) onComplete(x);
      return;
    }
    if (onUpdate) onUpdate(x, v);
    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);

  return {
    get value() {
      return x;
    },
    get velocity() {
      return v;
    },
    get finished() {
      return done;
    },
    /* Redirect from the live position AND the live velocity. */
    retarget(nextTarget, addVelocity = 0) {
      target = nextTarget;
      v += addVelocity;
      if (done) {
        done = false;
        last = null;
        raf = requestAnimationFrame(step);
      }
      return this;
    },
    stop() {
      done = true;
      if (raf != null) cancelAnimationFrame(raf);
      raf = null;
      return { value: x, velocity: v };
    },
  };
}

/* Where a flick would come to rest, using scroll deceleration rather than the
 * physics-textbook v^2/(2a). Snap to the target nearest THIS point, not to the
 * one nearest the release point — that is what makes a flick feel thrown. */
export function project(initialVelocity, decelerationRate = 0.998) {
  return ((initialVelocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/* Progressive resistance past a boundary. A hard stop reads as frozen; this
 * reads as responsive-but-there-is-nothing-more-here. */
export function rubberband(overshoot, dimension, constant = 0.55) {
  if (!dimension) return 0;
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

export function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}
